import { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  isUnlimitedOnlyEligibleAccount,
  paywallVariantOf,
  TPaywallVariant,
  UNLIMITED_ONLY_KILLED,
  UnlimitedOnlyBucketedIdState,
  UnlimitedOnlyPaywallState,
} from "MVVM/State/paywallExperiment";
import { getQueryParam } from "MVVM/Utilities/QueryParam";
import { abTestValue } from "MVVM/Utilities/AbTest";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { useIsInternalAccount } from "MVVM/Hooks/useIsInternalAccount";
import { useUser } from "MVVM/Hooks/useUser";

// Permanent: changing it reshuffles every user mid-experiment.
export const UNLIMITED_ONLY_SALT = "unlimited-only-paywall-v1";
export const UNLIMITED_ONLY_ROLLOUT = 0.5;

/**
 * ?unlimitedonly=1/0 QA override. Read once per page load, held in module
 * scope, and never written to the persisted atom, so the override ends with
 * the session instead of permanently reassigning the browser. Junk values
 * ("true", "yes") are not overrides and bucket normally. The exposure event
 * reports source query_param so the readout can drop these sessions from
 * both arms.
 */
let paramOverride: "1" | "0" | null | undefined;
const getParamOverride = () => {
  if (paramOverride === undefined) {
    const param = getQueryParam("unlimitedonly");
    paramOverride = param === "1" || param === "0" ? param : null;
  }
  return paramOverride;
};

// Module scope, not a ref: two callers mounting in the same tick would
// otherwise both hash and bucket the same id.
let bucketedFor: string | null = null;

// Resolved once for the page, not per hook instance. See useNewNav for the
// history: instances that resolved the id at different moments disagreed and
// rewrote the bucketed-id atom in a loop.
let resolvedAnonymousId: string | null = null;

// Kept so the exposure event can report the hash on the page load that
// bucketed; later loads read the persisted variant and never hash.
let bucketValue: number | null = null;

// One exposure per surface per page load; the readout dedupes the rest.
const exposedSurfaces = new Set<string>();

export const resetUnlimitedOnlyPaywallForTests = () => {
  paramOverride = undefined;
  bucketedFor = null;
  resolvedAnonymousId = null;
  bucketValue = null;
  exposedSurfaces.clear();
};

type TExposureSource = "query_param" | "internal_account" | "hash_bucket";

/**
 * Unlimited-only paywall experiment (ALL-1929): treatment sees a single
 * Unlimited card on the test surfaces instead of the tier grid.
 *
 * Pass `surface` from a test surface ("upgrade_page", "clip_eligibility") to
 * fire the exposure event when it renders; omit it to just read the variant.
 * Exposure is surface-scoped so the readout denominator is users who actually
 * saw a paywall, not everyone who loaded the app.
 *
 * The returned values latch on the instance's first render: a surface that
 * mounted before the async assignment settled stays on control (unassigned,
 * no exposure) for its whole lifetime instead of swapping arms mid-view.
 * SiteHeader mounts a bare instance app-wide so assignment settles at page
 * load, well before any paywall opens.
 */
export const useUnlimitedOnlyPaywall = (surface?: string) => {
  const [enabled, setEnabled] = useRecoilState(UnlimitedOnlyPaywallState);
  const [bucketedId, setBucketedId] = useRecoilState(
    UnlimitedOnlyBucketedIdState,
  );
  const { track } = useAnalyticsContext();
  const isInternal = useIsInternalAccount();
  // useAnalyticsContext rebuilds track every render; the effects must not.
  const trackRef = useRef(track);
  trackRef.current = track;

  const [anonymousId, setAnonymousId] = useState<string | null>(
    resolvedAnonymousId,
  );

  /**
   * Only ever read inside ready(). The snippet loads with
   * anonymousIdOptions.autoCapture from segment, so the id Rudder answers with
   * before it finishes initialising is replaced by the captured one, and
   * bucketing on the earlier value permanently disagrees with the id on events.
   */
  useEffect(() => {
    if (anonymousId) return;

    window.rudderanalytics?.ready?.(() => {
      resolvedAnonymousId =
        resolvedAnonymousId ??
        window.rudderanalytics?.getAnonymousId?.() ??
        null;
      setAnonymousId(resolvedAnonymousId);
    });
  }, [anonymousId]);

  /**
   * Internal accounts see treatment so the team QAs the new grid by default.
   * ?unlimitedonly=0 still wins: without that escape hatch nobody internal
   * could QA control. This does write the persisted atom, but internal
   * sessions are excluded from the readout, and on logout the bucketing
   * effect overwrites it (bucketedId was never set here).
   */
  useEffect(() => {
    if (!isInternal || enabled) return;
    if (getParamOverride() === "0") return;

    setEnabled(true);
  }, [isInternal, enabled, setEnabled]);

  useEffect(() => {
    if (!anonymousId) return;
    if (bucketedId === anonymousId || bucketedFor === anonymousId) return;
    // A real override is a QA session; don't mint an assignment under it.
    if (getParamOverride() !== null || isInternal) return;
    if (!window.crypto?.subtle) return;

    bucketedFor = anonymousId;

    abTestValue(anonymousId, UNLIMITED_ONLY_SALT).then((value) => {
      bucketValue = value;
      setBucketedId(anonymousId);
      setEnabled(value < UNLIMITED_ONLY_ROLLOUT);
    });
  }, [anonymousId, bucketedId, isInternal, setBucketedId, setEnabled]);

  const override = getParamOverride();
  const overridden = override !== null;
  // Not assigned until the variant is settled; the async hash landing after
  // a surface rendered must reach the next mount, not this one.
  const assigned =
    overridden ||
    (isInternal && enabled) ||
    (anonymousId !== null && bucketedId === anonymousId);
  const variant = paywallVariantOf(overridden ? override === "1" : enabled);

  // Latch on first render: the values a surface mounts with are the values
  // it keeps, so the grid and every event it tags agree for its lifetime.
  const latchedRef = useRef<{
    assigned: boolean;
    variant: TPaywallVariant;
    source: TExposureSource;
  } | null>(null);
  if (latchedRef.current === null) {
    latchedRef.current = {
      assigned,
      variant,
      source: overridden
        ? "query_param"
        : isInternal
        ? "internal_account"
        : "hash_bucket",
    };
  }
  const latched = latchedRef.current;

  useEffect(() => {
    if (!surface || UNLIMITED_ONLY_KILLED || !latched.assigned) return;
    if (exposedSurfaces.has(surface)) return;
    exposedSurfaces.add(surface);

    trackRef.current("Paywall Variant Assigned", {
      paywall_variant: latched.variant,
      surface,
      source: latched.source,
      ...(bucketValue !== null && { paywall_bucket_value: bucketValue }),
    });
    // latched is constant for the instance's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface]);

  return {
    unlimitedOnly: latched.assigned && latched.variant === "unlimited_only",
    variant: latched.variant,
    assigned: latched.assigned,
  };
};

/**
 * Non-latched variant read for eligibility gates (trial CTAs), never for test
 * surfaces — surfaces keep the latched hook so the grid and its events agree
 * for a view's lifetime. Gates need the opposite: nav and header instances
 * mount before the async assignment settles, latch control, and leak trials
 * into the treatment arm for the whole page load. This reads the persisted
 * assignment directly (synchronous for every bucketed browser) and re-renders
 * when a first-visit hash lands. Assignment itself still runs in the latched
 * hook's effects via SiteHeader's bare instance.
 */
export const useUnlimitedOnlyPaywallLive = () => {
  const enabled = useRecoilValue(UnlimitedOnlyPaywallState);
  const override = getParamOverride();
  const variant = paywallVariantOf(
    override !== null ? override === "1" : enabled,
  );
  return { unlimitedOnly: variant === "unlimited_only", variant };
};

/**
 * Whether the current account is in the treatment cohort: signed up after the
 * no-trial cutover. Internal accounts and the ?unlimitedonly=1 override bypass
 * the age gate (team accounts are old, and both are excluded from the
 * readout), so QA keeps working. AND this with the variant at every treatment
 * gate: existing users keep the grid and trial offer they signed up around.
 */
export const useUnlimitedOnlyEligibleAccount = () => {
  const { allstarUser } = useUser();
  const isInternal = useIsInternalAccount();
  return (
    isUnlimitedOnlyEligibleAccount(allstarUser?.user?._id) ||
    isInternal ||
    getParamOverride() === "1"
  );
};
